type LayoutSection = 'trading' | 'monitoring';

type SerializedPanel = {
  contentComponent?: string;
  params?: {
    type?: string;
    filterState?: unknown;
    [key: string]: unknown;
  };
};

type SerializedLayoutSpec = {
  dockViewLayout?: {
    panels?: Record<string, SerializedPanel>;
  };
};

type SerializedSpacesSectionConfig = {
  layouts?: SerializedLayoutSpec[];
  [key: string]: unknown;
};

type MigratableAppConfig = {
  spacesConfigs?: Partial<Record<LayoutSection, SerializedSpacesSectionConfig>>;
  layoutConfigs?: Partial<Record<LayoutSection, Record<string, SerializedLayoutSpec> | SerializedLayoutSpec[]>>;
};

const VALID_SECTIONS: readonly LayoutSection[] = ['trading', 'monitoring'];
const LEGACY_PRICE_GRID_PANEL_TYPE = 'priceGrid';
const PRICE_GRID_V2_PANEL_TYPE = 'priceGridV2';
const LEGACY_MULTILEG_PRICE_GRID_PANEL_TYPE = 'multilegPriceGrid';
const MULTILEG_PRICE_GRID_V2_PANEL_TYPE = 'multilegPriceGridV2';

const GENERIC_PRICE_GRID_DEFAULT_FILTER_STATE = {};
const MULTILEG_PRICE_GRID_DEFAULT_FILTER_STATE = {
  productTypes: ['Synthetic', 'Basis', 'CFD', 'CalendarSpread'],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

function isSerializedPanel(value: unknown): value is SerializedPanel {
  return isRecord(value);
}

function migrateLayoutSpec(layoutSpec: unknown) {
  if (!isRecord(layoutSpec)) {
    return;
  }

  const { dockViewLayout } = layoutSpec;
  if (!isRecord(dockViewLayout)) {
    return;
  }

  const { panels } = dockViewLayout;
  if (!isRecord(panels)) {
    return;
  }

  Object.values(panels)
    .filter(isSerializedPanel)
    .forEach(panel => {
      const shouldMigrateGeneric =
        panel.contentComponent === LEGACY_PRICE_GRID_PANEL_TYPE || panel.params?.type === LEGACY_PRICE_GRID_PANEL_TYPE;
      const shouldMigrateMultileg =
        panel.contentComponent === LEGACY_MULTILEG_PRICE_GRID_PANEL_TYPE ||
        panel.params?.type === LEGACY_MULTILEG_PRICE_GRID_PANEL_TYPE;

      if (!shouldMigrateGeneric && !shouldMigrateMultileg) {
        return;
      }

      const nextType = shouldMigrateMultileg ? MULTILEG_PRICE_GRID_V2_PANEL_TYPE : PRICE_GRID_V2_PANEL_TYPE;
      const defaultFilterState = shouldMigrateMultileg
        ? structuredClone(MULTILEG_PRICE_GRID_DEFAULT_FILTER_STATE)
        : structuredClone(GENERIC_PRICE_GRID_DEFAULT_FILTER_STATE);

      panel.contentComponent = nextType;
      panel.params = {
        ...panel.params,
        type: nextType,
        filterState: defaultFilterState,
      };
    });
}

function migrateLayoutCollection(layoutCollection: unknown) {
  if (layoutCollection == null) {
    return;
  }

  if (Array.isArray(layoutCollection)) {
    layoutCollection.forEach(migrateLayoutSpec);
    return;
  }

  if (typeof layoutCollection === 'object') {
    Object.values(layoutCollection).forEach(migrateLayoutSpec);
  }
}

/**
 * Rewrites serialized panels from removed legacy Price Grid types to the current Price Grid panels.
 * Legacy filter state is reset to the target panel defaults because old filter fields may not match the new variants.
 */
export function migrateLegacyPriceGridLayouts(config: MigratableAppConfig): void {
  const spacesConfigs = config.spacesConfigs;

  for (const section of VALID_SECTIONS) {
    migrateLayoutCollection(spacesConfigs?.[section]?.layouts);
    migrateLayoutCollection(config.layoutConfigs?.[section]);
  }
}
